/**
 * Read-only sidebar row for a single chat thread. Clicking navigates to the chat;
 * the vertical-dots button opens the edit/delete menu. Renders status dots for
 * the thread's background tasks.
 */
import React from "react";
import { FiMoreVertical } from "react-icons/fi";
import { RiChat4Line } from "react-icons/ri";
import { useNavigate } from "react-router";

import { completionDotLabel } from "types/TaskRegistry";

import type { ThreadTaskIndicator } from "../utilities/threadTaskIndicators";
import styles from "../ChatHistory.module.scss";

interface ReadOnlyChatHistoryElementProps {
  chatId: string;
  chatName: string;
  onEditButtonClick: (chatId: string) => void;
  active: boolean;
  isSelected: boolean;
  hasSelection: boolean;
  onToggleSelect: (chatId: string, shiftKey: boolean) => void;
  /** Undefined when there is nothing to show for this thread. */
  taskIndicator?: ThreadTaskIndicator;
}

export default function ReadOnlyChatHistoryElement({
  chatId,
  chatName,
  onEditButtonClick,
  active,
  isSelected,
  hasSelection,
  onToggleSelect,
  taskIndicator,
}: ReadOnlyChatHistoryElementProps) {
  const navigate = useNavigate();

  const threadClass = active
    ? styles.chatThread + " " + styles.selected
    : styles.chatThread;

  const handleRowClick = () => {
    navigate(`/chats/${chatId}`);
  };

  const dotClass = taskIndicator?.dotClass ?? null;
  const hasUnreadCompletion = taskIndicator?.hasUnreadCompletion ?? false;
  const unreadFailed = taskIndicator?.unreadFailed ?? false;
  const completionLabel = completionDotLabel(unreadFailed);

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEditButtonClick(chatId);
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleSelect(chatId, e.shiftKey);
  };

  const checkboxClass = hasSelection
    ? styles.selectCheckbox
    : `${styles.selectCheckbox} ${styles.hoverOnly}`;

  return (
    <div className={threadClass} onClick={handleRowClick}>
      <span className={styles.threadSpan}>
        <span className={styles.chatIconWrap}>
          <RiChat4Line className={styles.chatIcon} />
          {dotClass && (
            <span className={`${styles.taskDot} ${dotClass}`}>●</span>
          )}
          {hasUnreadCompletion && (
            // Unlike the clipboard button's dot, this one carries its own
            // accessible name and takes pointer events, so the hover tooltip fires.
            <span
              role="img"
              aria-label={completionLabel}
              title={`${completionLabel} — click to view`}
              className={`${styles.unreadCompletionDot} ${
                unreadFailed ? styles.unreadCompletionFailed : ""
              }`}
            />
          )}
        </span>
        <span className={styles.threadName}>{chatName}</span>
      </span>
      <span className={styles.checkboxLabel} onClick={handleCheckboxClick}>
        <input
          type="checkbox"
          className={checkboxClass}
          checked={isSelected}
          readOnly
        />
      </span>
      <button className={styles.innerInputButton} onClick={handleEditClick}>
        <FiMoreVertical />
      </button>
    </div>
  );
}
